import { getResourceKey$GetGalleryArtwork } from "kreate-server/commands/GetGalleryArtwork/fetcher";
import { handler$GetGalleryArtwork } from "kreate-server/commands/GetGalleryArtwork/handler";
import { GetGalleryArtwork$Result } from "kreate-server/commands/GetGalleryArtwork/typing";
import { getResourceKey$LoginStatus } from "kreate-server/commands/LoginStatus/fetcher";
import { handler$LoginStatus } from "kreate-server/commands/LoginStatus/handler";
import { LoginStatus$Result } from "kreate-server/commands/LoginStatus/typing";
import { GetServerSideProps } from "next";
import { SWRConfig, unstable_serialize } from "swr";
import { z } from "zod";

import PageGalleryArtwork from "../../../containers/PageGalleryArtwork";
import { formatFallback, omitUndefinedRecursively } from "../../../utils";

import { intentionallyIgnoreError } from "@/modules/common-utils/assert";
import { getDb } from "@/modules/mongodb";

type Props = {
  pageProps: React.ComponentProps<typeof PageGalleryArtwork>;
  fallback: {
    LoginStatus: LoginStatus$Result | undefined;
    GetGalleryArtwork: GetGalleryArtwork$Result | undefined;
  };
};

export default function Route({ pageProps, fallback }: Props) {
  return (
    <SWRConfig
      value={{
        fallback: formatFallback({
          [unstable_serialize(getResourceKey$LoginStatus())]:
            fallback.LoginStatus,
          [unstable_serialize(
            getResourceKey$GetGalleryArtwork({ assetId: pageProps.assetId })
          )]: fallback.GetGalleryArtwork,
        }),
      }}
    >
      <PageGalleryArtwork {...pageProps} />
    </SWRConfig>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const Query = z.object({ suffix: z.string() });
  const querySP = Query.safeParse(context.query);
  if (!querySP.success) {
    return { notFound: true };
  }

  const { suffix } = querySP.data;
  const assetFingerprint = "asset1" + suffix;

  const db = await getDb();
  const [fallback_LoginStatus, fallback_GetGalleryArtwork] = await Promise.all([
    handler$LoginStatus(db, {
      token: context.req.cookies["token"],
    }).catch(intentionallyIgnoreError),
    handler$GetGalleryArtwork(
      { db },
      {
        assetFingerprint,
      }
    ).catch(intentionallyIgnoreError),
  ]);

  if (!fallback_GetGalleryArtwork) {
    return { notFound: true };
  }

  return {
    props: omitUndefinedRecursively({
      pageProps: { assetId: fallback_GetGalleryArtwork.assetId },
      fallback: {
        LoginStatus: fallback_LoginStatus,
        GetGalleryArtwork: fallback_GetGalleryArtwork,
      },
    }),
  };
};
